// « Mise en route » — /onboarding (Backend docs/onboarding.md). The steps a new
// account goes through, chosen by what it confirms it is (logistician or
// e-merchant). Each step's state comes from the account's data (GET
// /api/setup/onboarding): a step is done because the carrier, the client, the
// channel or the source exists. Simple things are done right here (profile,
// carriers from the catalogue, B2B clients); the rest opens the page that sets
// it up and comes back. ?step=<key> opens a step (copilot, sidebar, links).
// Every write dispatches ONBOARDING_EVENT (events.js) so the sidebar entry
// refreshes its progress.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, EyeOff, Flag, ListChecks } from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, Meter, PageHeader, Steps,
} from '../../design/ui';
import { StepPanel, STEP_TEXT } from './OnboardingSteps';
import { notifyOnboardingChanged } from './events';
import './Onboarding.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const STATUS_TEXT = { done: 'Fait', pending: 'À faire', skipped: 'Plus tard' };

const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => searchParams.get('step'));
  const [busy, setBusy] = useState(false);
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/setup/onboarding', authHeaders());
      setStatus(res.data);
      setError('');
    } catch (e) {
      setError(t('onboarding.loadError', 'La mise en route n’a pas pu être chargée.'));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);
  // Coming back from a settings page (OAuth, a source, a carrier) refreshes the facts.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const steps = status?.steps || [];
  const known = steps.find((s) => s.key === selected);
  const current = known || steps.find((s) => s.key === status?.current_step) || steps[0];
  const index = current ? steps.indexOf(current) : -1;

  const select = (key) => {
    setSelected(key);
    const next = new URLSearchParams(searchParams);
    next.set('step', key);
    setSearchParams(next, { replace: true });
  };

  // Every write returns the fresh status: the page never guesses.
  const run = async (request) => {
    setBusy(true);
    setError('');
    try {
      const res = await request();
      if (res?.data?.steps) setStatus(res.data);
      notifyOnboardingChanged();
      return res;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('onboarding.saveError', 'Le changement n’a pas pu être enregistré.'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const actions = {
    setProfile: (profile) => run(() => axios.put('/api/setup/onboarding/profile', { profile }, authHeaders())),
    decide: (step, decision, mode) => run(() => axios.post(`/api/setup/onboarding/steps/${step}`, { decision, ...(mode ? { mode } : {}) }, authHeaders())),
    refresh: load,
    busy,
  };

  const goNext = () => {
    const next = steps.slice(index + 1).find((s) => s.status !== 'done') || steps[index + 1];
    if (next) select(next.key);
  };

  const finish = async () => {
    const res = await run(() => axios.post('/api/setup/onboarding/finish', {}, authHeaders()));
    if (res) navigate('/dashboard');
  };

  const dismiss = async () => {
    const res = await run(() => axios.post('/api/setup/onboarding/dismiss', {}, authHeaders()));
    setConfirmDismiss(false);
    if (res) navigate('/dashboard');
  };

  const stepTitle = (key) => t(`onboarding.steps.${key}.title`, STEP_TEXT[key]?.[0] || key);

  if (!status) {
    return (
      <div className="u-page onboarding-v2">
        <PageHeader title={t('onboarding.title', 'Mise en route')} />
        <div className="u-well">
          {error ? (
            <Banner
              tone="danger"
              title={error}
              actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={load}>{t('teamStatsUi.retry', 'Réessayer')}</button>}
            />
          ) : (
            <div className="onboarding-v2__layout" aria-busy="true">
              <span className="u-skeleton onboarding-v2__skeleton onboarding-v2__skeleton--steps" aria-hidden="true" />
              <span className="u-skeleton onboarding-v2__skeleton" aria-hidden="true" />
              <span className="onboarding-v2__sr">{t('common.loading', 'Chargement…')}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const remaining = Math.max(0, (status.total || 0) - (status.done || 0));
  const subtitle = status.completed
    ? t('teamStatsUi.onboarding.subtitleDone', 'Les {{total}} étapes sont faites · vous pouvez revenir sur chacune', { total: status.total })
    : t('teamStatsUi.onboarding.subtitle', '{{done}} étapes faites sur {{total}} · {{remaining}} à faire pour que chaque ticket soit traité de bout en bout', {
      done: status.done, total: status.total, remaining,
    });

  const canPutOff = Boolean(current && current.status === 'pending' && current.accepts.includes('skipped'));
  const hasNext = index < steps.length - 1;

  const stepItems = steps.map((step) => {
    const isSelected = step.key === current?.key;
    return {
      key: step.key,
      status: step.status === 'done' ? 'done' : isSelected ? 'current' : 'todo',
      title: (
        <button
          type="button"
          className={`onboarding-v2__step-btn${isSelected ? ' is-selected' : ''}`}
          aria-current={isSelected ? 'step' : undefined}
          onClick={() => select(step.key)}
        >
          {stepTitle(step.key)}
        </button>
      ),
      description: (
        <span className={`onboarding-v2__step-status onboarding-v2__step-status--${step.status}`}>
          {t(`onboarding.status.${step.status}`, STATUS_TEXT[step.status])}
        </span>
      ),
    };
  });

  return (
    <div className="u-page onboarding-v2">
      <PageHeader
        title={t('onboarding.title', 'Mise en route')}
        subtitle={subtitle}
        actions={!status.completed && status.started ? (
          <button type="button" className="u-btn u-btn--secondary" onClick={() => setConfirmDismiss(true)}>
            <EyeOff aria-hidden="true" />
            {t('onboarding.dismiss', 'Masquer la mise en route')}
          </button>
        ) : null}
      />

      <div className="u-well">
        {status.completed && (
          <Banner tone="success">
            {status.dismissed
              ? t('onboarding.dismissedBanner', 'Mise en route masquée. Les étapes restent consultables ici.')
              : t('onboarding.completedBanner', 'Mise en route terminée. Vous pouvez revenir sur chaque étape quand vous voulez.')}
          </Banner>
        )}
        {!status.completed && status.can_finish && (
          <Banner
            tone="success"
            title={t('onboarding.readyBanner', 'Tout est prêt : vos tickets peuvent être traités de bout en bout.')}
            actions={(
              <button type="button" className="u-btn u-btn--primary" disabled={busy} onClick={finish}>
                {t('onboarding.finish', 'Terminer la mise en route')}
              </button>
            )}
          />
        )}
        {error && <Banner tone="danger">{error}</Banner>}

        <div className="onboarding-v2__layout">
          <Card
            icon={ListChecks}
            title={t('onboarding.stepsLabel', 'Étapes')}
            badge={<Badge tone={status.done === status.total ? 'success' : 'accent'}>{`${status.done} / ${status.total}`}</Badge>}
            className="onboarding-v2__steps"
          >
            <Meter
              value={status.done}
              max={status.total || 1}
              tone={status.done === status.total ? 'success' : undefined}
              label={t('onboarding.progressLabel', 'Étapes faites')}
            />
            <nav aria-label={t('onboarding.stepsLabel', 'Étapes')} className="onboarding-v2__steps-nav">
              <Steps steps={stepItems} />
            </nav>
          </Card>

          {current && (
            <section className="u-card onboarding-v2__panel" aria-labelledby="onboarding-v2-panel-title">
              <div className="u-card__header">
                <span className="u-icon-chip" aria-hidden="true"><Flag strokeWidth={1.8} /></span>
                <div className="u-card__heading">
                  <span className="u-overline">{t('onboarding.stepOf', 'Étape {{n}} sur {{total}}', { n: index + 1, total: steps.length })}</span>
                  <h2 className="u-card__title" id="onboarding-v2-panel-title">{stepTitle(current.key)}</h2>
                  <p className="u-card__desc">{t(`onboarding.steps.${current.key}.why`, STEP_TEXT[current.key]?.[1] || '')}</p>
                </div>
                <div className="u-card__actions">
                  {current.status === 'done' && <Badge tone="success" dot>{t('onboarding.status.done', STATUS_TEXT.done)}</Badge>}
                  {current.status === 'skipped' && <Badge tone="warning" dot>{t('onboarding.status.skipped', STATUS_TEXT.skipped)}</Badge>}
                </div>
              </div>

              <div className="u-card__body">
                <StepPanel step={current} status={status} actions={actions} />
              </div>

              {(canPutOff || current.decision || hasNext) && (
              <div className="u-card__footer onboarding-v2__footer">
                <div className="onboarding-v2__footer-left">
                  {canPutOff && (
                    <button type="button" className="u-btn u-btn--ghost" disabled={busy} onClick={async () => { await actions.decide(current.key, 'skipped'); goNext(); }}>
                      {t('onboarding.later', 'Plus tard')}
                    </button>
                  )}
                  {current.decision && (
                    <button type="button" className="u-btn u-btn--ghost" disabled={busy} onClick={() => actions.decide(current.key, 'reopen')}>
                      {t('onboarding.reopen', 'Revenir sur ce choix')}
                    </button>
                  )}
                </div>
                {hasNext && (
                  <button type="button" className="u-btn u-btn--secondary" onClick={goNext}>
                    {t('onboarding.next', 'Étape suivante')} <ArrowRight aria-hidden="true" />
                  </button>
                )}
              </div>
              )}
            </section>
          )}
        </div>
      </div>

      {confirmDismiss && (
        <ConfirmDialog
          icon={EyeOff}
          title={t('teamStatsUi.onboarding.dismissTitle', 'Masquer la mise en route ?')}
          message={t('teamStatsUi.onboarding.dismissMessage', 'Le ticket de démonstration sera supprimé et la mise en route quittera le haut du menu. Ses étapes restent consultables depuis Paramètres.')}
          confirmLabel={t('onboarding.dismissYes', 'Masquer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          onConfirm={dismiss}
          onCancel={() => !busy && setConfirmDismiss(false)}
          busy={busy}
        />
      )}
    </div>
  );
};

export default Onboarding;
